import React, { createContext, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

import GeraPdf from '../pdf/protocolo';
import { belemApi } from '../services/belemApi';
import { brDateToISO } from '../utils/date';
import { normalizeText } from '../utils/text';
import { getErrorMessage } from '../utils/http';

export const EscolaContext = createContext({});

// LocalStorage keys (single source of truth)
const LS = {
  ESCOLA: 'escola',
  ALUNO: 'aluno',
  CPF: 'Cpf',
  ATT: 'Att',
  COUNT: 'count',
};

function safeSet(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // ignore
  }
}

function safeGet(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

function safeRemove(keys = []) {
  keys.forEach((k) => {
    try {
      localStorage.removeItem(k);
    } catch {
      // ignore
    }
  });
}

export default function EscolaProvider({ children }) {
  const navigate = useNavigate();

  const [escolas, setEscolas] = useState([]);
  const [escola, setEscola] = useState([]);
  const [escolaC, setEscolaC] = useState(() => safeGet(LS.ESCOLA, ''));
  const [prvid, setPrvid] = useState('');

  const [aluno, setAluno] = useState([]);
  const [alunoC, setAlunoC] = useState(() => safeGet(LS.ALUNO, ''));

  // Dados validados
  const [maeAluno, setMaeAluno] = useState('');
  const [nasc, setNasc] = useState(''); // ISO YYYY-MM-DD
  const [CPFAluno, setCPFAluno] = useState('');
  const [form, setForm] = useState(null);

  const [escolaInput, setEscolaInput] = useState('');
  const [att, setAtt] = useState('');

  const pesquisaEscola = async (input) => {
    setEscolaInput(input);
    const trimmed = String(input || '').trim();
    if (!trimmed) {
      toast.info('Preencha a escola!');
      return;
    }

    const escolaNormalizada = normalizeText(trimmed);
    try {
      const { data } = await belemApi.pesquisaEscola(escolaNormalizada);
      setEscolas(Array.isArray(data) ? data : []);
      if (!data || data.length === 0) {
        toast.error(`Escola ${trimmed} não encontrada`);
      }
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const consultaEscola = async (PRV_ID) => {
    const escolaCons = PRV_ID;
    if (!escolaCons) {
      toast.info('Selecione uma escola.');
      return;
    }

    try {
      const { data } = await belemApi.verifyEscola(escolaCons);
      setEscola(data);

      const loggedEscola = data?.[2] ?? '';
      setEscolaC(loggedEscola);
      safeSet(LS.ESCOLA, loggedEscola);

      const prv = data?.[1] ?? '';
      setPrvid(prv);
      if (!prv) toast.error('Escola não informada neste ano!');
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const pesquisaAluno = async (inputAluno) => {
    const trimmed = String(inputAluno || '').trim();
    if (!trimmed) {
      toast.info('Preencha o nome!');
      return;
    }
    if (!prvid) {
      toast.info('Selecione a escola primeiro.');
      return;
    }

    const alunoNormalizado = normalizeText(trimmed);
    try {
      const { data } = await belemApi.pesquisaAluno(alunoNormalizado, prvid);
      setAluno(Array.isArray(data) ? data : []);
      if (!data || data.length === 0) {
        toast.error(`Aluno(a) ${alunoNormalizado} não encontrado`);
        return;
      }

      const loggedAluno = data?.[0]?.BEN_ALUNO ?? '';
      setAlunoC(loggedAluno);
      safeSet(LS.ALUNO, loggedAluno);

      // Alguns backends retornam status; mantenho a regra antiga.
      if (data?.status === false) {
        toast.error('Aluno não encontrado');
        return;
      }
      navigate('/aluno');
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const verifyForm = async (payload) => {
    const escolaDados = payload?.escola;
    const alunoDados = payload?.nome;
    const maeDados = normalizeText(payload?.mae || '');
    const cpfDados = String(payload?.CPF || '').trim();
    const datanascIso = brDateToISO(payload?.datanasc || '');

    if (!escolaDados) {
      navigate('/pesquisaEscola');
      toast.error('Campo escola vazio');
      return;
    }
    if (!alunoDados) {
      navigate('/pesquisaEscola');
      toast.error('Campo aluno vazio');
      return;
    }
    if (!maeDados) {
      toast.error('Campo mãe vazio');
      return;
    }
    if (!cpfDados) {
      toast.error('Campo CPF vazio');
      return;
    }
    if (!datanascIso) {
      toast.error('Data de nascimento inválida');
      return;
    }

    try {
      const { data } = await belemApi.verifyForm(escolaDados, alunoDados, maeDados.trim(), cpfDados, datanascIso);
      setForm(data?.outBinds ?? null);
      setMaeAluno(maeDados);
      setCPFAluno(cpfDados);
      setNasc(datanascIso);

      // Mantém comportamento: API manda msg em outBinds[1]
      const msg = data?.outBinds?.[1];
      if (msg) toast.error(msg);

      if (data?.outBinds?.[0] === 0) {
        navigate('/agendar');
      }
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const AgendaAtendimento = async (inputHora, mae, nascIso, cpf, _cdata1, alunoAgend) => {
    const attId = inputHora;
    if (!attId) {
      toast.info('Selecione um horário.');
      return;
    }
    if (!alunoAgend) {
      toast.error('Aluno inválido. Volte e selecione novamente.');
      navigate('/pesquisaEscola');
      return;
    }

    try {
      setAtt(attId);
      const { data } = await belemApi.criarAgendamento(attId, alunoAgend, mae, nascIso, cpf);

      const localCpf = data?.[0];
      const localCount = data?.[1];
      safeSet(LS.CPF, localCpf);
      safeSet(LS.ATT, attId);
      safeSet(LS.COUNT, localCount);

      // Regra existente: se count === 0, gera PDF e finaliza
      if (localCount === 0) {
        const cpfAgendado = safeGet(LS.CPF, '');
        const attStored = safeGet(LS.ATT, '');

        const conf = await belemApi.confirmaPdf(cpfAgendado, attStored);
        const respost = conf.data;
        if (respost) {
          GeraPdf(respost);
          safeRemove([LS.ALUNO, LS.ATT, LS.ESCOLA, LS.COUNT, LS.CPF]);
          navigate('/');
          toast.success('Agendamento realizado com sucesso!');
        } else {
          toast.error('Não foi possível gerar o protocolo.');
          navigate('/');
        }
      }
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const duvida = () => navigate('/ajuda');

  const value = useMemo(
    () => ({
      // state
      escolas,
      escola,
      escolaC,
      aluno,
      alunoC,
      maeAluno,
      nasc,
      CPFAluno,
      form,
      prvid,
      att,
      escolaInput,

      // setters (expose only what is needed)
      setEscolas,
      setEscola,
      setEscolaC,
      setAluno,
      setAlunoC,
      setAtt,

      // actions
      pesquisaEscola,
      consultaEscola,
      pesquisaAluno,
      verifyForm,
      AgendaAtendimento,
      duvida,
    }),
    [
      escolas,
      escola,
      escolaC,
      aluno,
      alunoC,
      maeAluno,
      nasc,
      CPFAluno,
      form,
      prvid,
      att,
      escolaInput,
    ]
  );

  return <EscolaContext.Provider value={value}>{children}</EscolaContext.Provider>;
}
